<template>
  <card-dialog
    :value="value"
    @input="val => $emit('input', val)"
    :title="isEditing ? $t('label.edit_place') : $t('label.create_place')"
    :width="640"
  >
    <v-form v-model="valid" ref="form">
      <form-field :label="$t('field.name')" block>
        <text-field
          v-model="form.name"
          :rules="[noDuplicates, required($t('field.name'))]"
          data-testid="place_name"
        />
      </form-field>
      <form-field :label="$mpt($t('field.sensors'))" block>
        <monitoring-point-selector
          v-model="form.entity_ids"
          :sensors="selectableSensors"
          :visible="value"
          class="mp-picker"
        />
        <monitoring-points-used-by-other-places :sensors="sensorsUsedByOtherPlaces" />
      </form-field>
    </v-form>
    <template #actions>
      <dialog-action @click="close" data-testid="cancel_button">{{ $t('button.cancel') }}</dialog-action>
      <dialog-action
        @click.stop="save"
        :disabled="saving || !valid || !hasChanges"
        data-testid="save_button"
      >
        {{ saving ? $t('button.saving') : (isEditing ? $t('button.save') : $t('button.create')) }}
      </dialog-action>
    </template>
  </card-dialog>
</template>

<i18n lang="yaml">
ja:
  label:
    create_place: 管理施設の作成
    edit_place: 管理施設の編集
  button:
    cancel: キャンセル
    save: 保存
    saving: 保存中...
    create: 作成
  field:
    name: 名称
    sensors: センサー
  messages:
    place_with_name_exists: この名称は既に使用されています
    create_success: '管理施設「{placeName}」を作成しました'
    update_success: '管理施設「{placeName}」を更新しました'
  errors:
    name_already_exists: この名称は既に使用されています
    invalid_sensor: 無効なセンサーが含まれています
    sensor_already_assigned: 選択したセンサーは既に別の管理施設に割り当てられています
    unknown: 保存に失敗しました
en:
  label:
    create_place: Create Facility
    edit_place: Edit Facility
  button:
    cancel: Cancel
    save: Save
    saving: Saving...
    create: Create
  field:
    name: Name
    sensors: Sensors
  messages:
    place_with_name_exists: This name is already in use
    create_success: 'Facility "{placeName}" was created'
    update_success: 'Facility "{placeName}" was updated'
  errors:
    name_already_exists: This name is already in use
    invalid_sensor: Invalid sensor included
    sensor_already_assigned: Selected sensor is already assigned to another facility
    unknown: Failed to save
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import Validations from '@/components/Form/mixins/form-validations';

import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import MonitoringPointSelector from '@/components/molecules/MonitoringPointSelector';
import MonitoringPointsUsedByOtherPlaces from '@/components/pages/RTypePlaces/MonitoringPointsUsedByOtherPlaces';

const API_ERROR_MESSAGES = {
  'Name already exists': 'errors.name_already_exists',
  'Invalid sensor': 'errors.invalid_sensor',
  'Sensor already assigned to another place': 'errors.sensor_already_assigned',
};

export default {
  name: 'r-type-place-editor',
  mixins: [
    Notifications,
    Validations,
  ],
  props: {
    value: {
      type: Boolean,
    },
    place: {
      type: Object,
      default: null,
    },
  },
  components: {
    CardDialog,
    DialogAction,
    FormField,
    TextField,
    MonitoringPointSelector,
    MonitoringPointsUsedByOtherPlaces,
  },
  data() {
    return {
      form: {
        name: '',
        entity_ids: [],
      },
      valid: false,
      saving: false,
    };
  },
  inject: ['api', 'getRTypePlaces', 'getRTypeSensors'],
  computed: {
    isEditing() {
      return this.place != null;
    },
    ratSensors() {
      return this.getRTypeSensors();
    },
    ownedSensors() {
      return this.ratSensors.filter(s => s.permissions.includes('full'));
    },
    ownedEntityIds() {
      if (this.place == null) return [];
      const ownedSensorIds = new Set(this.ownedSensors.map(s => s.id));
      return this.place.entity_ids.filter(id => ownedSensorIds.has(id));
    },
    assignedSensorMap() {
      const map = {};
      this.getRTypePlaces().forEach(p => {
        if (this.place && p.id === this.place.id) return;
        p.entity_ids.forEach(sensorId => {
          map[sensorId] = p.name;
        });
      });
      return map;
    },
    sensorOptions() {
      return this.ownedSensors.map(sensor => ({
        ...sensor,
        assignedToPlace: this.assignedSensorMap[sensor.id] || null,
      }));
    },
    selectableSensors() {
      return this.sensorOptions.filter(s => !s.assignedToPlace);
    },
    sensorsUsedByOtherPlaces() {
      return this.sensorOptions.filter(s => s.assignedToPlace);
    },
    hasChanges() {
      if (!this.isEditing) {
        return this.form.name.trim() !== '' || this.form.entity_ids.length > 0;
      }
      const nameChanged = this.form.name !== this.place.name;
      const idsChanged = this._.xor(this.form.entity_ids, this.ownedEntityIds).length > 0;
      return nameChanged || idsChanged;
    },
  },
  methods: {
    save: async function () {
      try {
        this.saving = true;
        if (this.isEditing) {
          await this.update();
        } else {
          await this.create();
        }
        this.close();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.saving = false;
      }
    },
    async create() {
      const created = await this.api.createRTypePlace({
        name: this.form.name,
        entity_ids: this.form.entity_ids,
      });
      this.notifySuccess('messages.create_success', { placeName: created.name });
      this.$emit('saved', created);
    },
    async update() {
      const body = {
        entity_ids: this.form.entity_ids,
      };
      if (this.form.name !== this.place.name) {
        body.name = this.form.name;
      }
      const updated = await this.api.updateRTypePlace(this.place.id, body);
      this.notifySuccess('messages.update_success', { placeName: updated.name });
      this.$emit('saved', updated);
    },
    handleApiError(error) {
      const apiError = error?.response?.data?.error;
      const messageKey = API_ERROR_MESSAGES[apiError] || 'errors.unknown';
      this.notifyError(messageKey);
    },
    close() {
      this.$emit('input', false);
    },
    noDuplicates() {
      if (this.isEditing && this.form.name === this.place.name) {
        return true;
      }
      const placeWithSameName = this.getRTypePlaces().find(
        p => p.name === this.form.name,
      );
      return !placeWithSameName || this.$t('messages.place_with_name_exists');
    },
    resetForm() {
      this.form.name = this.place != null ? this.place.name : '';
      this.form.entity_ids = [...this.ownedEntityIds];
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
  watch: {
    place() {
      this.resetForm();
    },
    value(val) {
      if (val) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped lang="sass">
// 面の色を変えて「記入する欄」ではなく「一覧から選ぶ場所」だと見た目で示す。
// 高さを固定しないと件数の分だけ伸びてダイアログ側のスクロールになるため、
// ここで高さを決めて欄の中でスクロールさせる。
// 面の色は子（絞り込み欄・見出しの固定表示）にも必要なため CSS 変数で供給する
.mp-picker
  --mp-surface: #f5f5f5
  // この欄自体は余白を持たないため、一覧の内側余白を子へ指定する
  --mp-list-inset: 10px

  height: 387px
  overflow-y: auto
  background: var(--mp-surface)
  border-radius: 4px
</style>
